<template>
  <section
    class="SettingsPage"
    data-theme="light"
  >
    <div class="SettingsPage__content">
      <section class="SettingsPage__section">
        <h2 class="SettingsPage__sectionTitle">
          Notifications
        </h2>
        <div class="SettingsPage__settingList">
          <label
            class="SettingsPage__setting"
            data-testid="runSummaryNotificationSetting"
            :aria-busy="isUpdatingRunSummarySetting"
          >
            <h3 class="SettingsPage__settingTitle">
              Daily summaries leading up to runs
            </h3>
            <div class="FormToggle">
              <input
                v-model="form.is_run_summary_notification_enabled"
                class="FormToggle__input"
                type="checkbox"
                :disabled="isUpdatingRunSummarySetting"
                @input="handleRunSummaryToggle"
              >
            </div>
          </label>
          <label
            class="SettingsPage__setting"
            data-testid="updatedStatusesNotificationSetting"
            :aria-busy="isUpdatingUpdatedStatusesSetting"
          >
            <h3 class="SettingsPage__settingTitle">
              Individual player updates for runs
            </h3>
            <div class="FormToggle">
              <input
                v-model="form.is_updated_statuses_notification_enabled"
                class="FormToggle__input"
                type="checkbox"
                :disabled="isUpdatingUpdatedStatusesSetting"
                @input="handleUpdatedStatusesToggle"
              >
            </div>
          </label>
        </div>
      </section>
    </div>
    <div class="SettingsPage__signOutButtonContainer">
      <FormButton
        @click="handleSignOutClick"
      >
        Sign out
      </FormButton>
    </div>
    <footer class="SettingsPage__footer">
      <a
        href="#"
        @click.prevent="handleVersionClick"
      >Version {{ version }}</a>
    </footer>
  </section>
</template>

<script lang="ts" setup>
import FormButton from "@/components/FormButton.vue";
import { supabaseClient } from "@/lib/database/supabase";
import haptic from "@/lib/utils/platform/haptic";
import useAuthStore from "@/stores/useAuthStore";
import { Player } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

const authStore = useAuthStore();

const {
  signOut,
} = authStore;

const {
  currentUser,
  currentPlayer,
} = storeToRefs(authStore);

const form = ref<Pick<Player, "is_updated_statuses_notification_enabled" | "is_run_summary_notification_enabled">>({
  is_updated_statuses_notification_enabled: true,
  is_run_summary_notification_enabled: true,
});
const isUpdatingUpdatedStatusesSetting = ref(false);
const isUpdatingRunSummarySetting = ref(false);

watch(currentPlayer, () => {
  if (currentPlayer.value) {
    form.value.is_updated_statuses_notification_enabled = currentPlayer.value.is_updated_statuses_notification_enabled;
    form.value.is_run_summary_notification_enabled = currentPlayer.value.is_run_summary_notification_enabled;
  }
}, {
  immediate: true,
});

const version = computed(() => String(import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA).substring(0, 7));

async function handleUpdatedStatusesToggle () {
  if (!currentUser.value || isUpdatingUpdatedStatusesSetting.value) return;

  haptic();

  isUpdatingUpdatedStatusesSetting.value = true;
  const {
    error,
  } = await supabaseClient
    .from("players")
    .update({
      is_updated_statuses_notification_enabled: !form.value.is_updated_statuses_notification_enabled,
    })
    .eq("user_id", currentUser.value.id);
  isUpdatingUpdatedStatusesSetting.value = false;

  if (error) {
    alert(error.message);
  }
}

async function handleRunSummaryToggle () {
  if (!currentUser.value || isUpdatingRunSummarySetting.value) return;

  haptic();

  isUpdatingRunSummarySetting.value = true;
  const {
    error,
  } = await supabaseClient
    .from("players")
    .update({
      is_run_summary_notification_enabled: !form.value.is_run_summary_notification_enabled,
    })
    .eq("user_id", currentUser.value.id);
  isUpdatingRunSummarySetting.value = false;

  if (error) {
    alert(error.message);
  }
}

async function handleSignOutClick () {
  try {
    await signOut();
  } catch (err) {
    alert((err as Error).message);
  }
}

function handleVersionClick () {
  window.location.reload();
}
</script>

<style lang="scss" scoped>
.SettingsPage {
  display: grid;
  grid: auto min-content min-content / auto;
  height: 100%;
  padding: 0 0 calc(-6px + var(--safeAreaInsetBottom));

  background-color: var(--themeColorSurface);

  font-stretch: var(--fontWidthExtended);
  line-height: 1.5;
}

.SettingsPage__content {
  display: grid;
  align-content: start;
}

.SettingsPage__sectionTitle {
  padding: 32px 16px 12px;

  border-bottom: solid 1px var(--themeColorNeutral100);

  font: var(--fontWeightMedium) 18px / 1 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
}

.SettingsPage__settingList {
  display: grid;
  gap: 1px;
  border-bottom: solid 1px var(--themeColorNeutral100);
}

.SettingsPage__setting {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;

  outline: dashed 1px var(--themeColorNeutral100);
}

.SettingsPage__settingTitle {
  position: relative;
  top: var(--fontSafariOffset);
}

.SettingsPage__signOutButtonContainer {
  display: grid;
  padding: 0 16px;
}

.SettingsPage__footer {
  margin-top: 14px;
  padding: 0 16px;

  font-size: 11px;
  text-align: center;
}

.FormToggle {
  position: relative;

  width: 56px;
  height: 32px;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 9999px;
  background-color: var(--themeColorWhite);
  box-shadow: 0 0 0 0 transparent;

  cursor: pointer;

  transition: 350ms box-shadow;

  &:has(:focus-visible) {
    box-shadow: 0 0 0 3px var(--themeColorSecondary);
  }

  &::after {
    position: absolute;
    top: 4px;
    left: 4px;

    height: 20px;
    aspect-ratio: 1;

    border-radius: 50%;
    background-color: var(--themeColorNeutral300);

    transition: 250ms;
    transition-property: background-color, transform;

    content: "";
  }

  &:has(:checked)::after {
    transform: translateX(calc(4px + 100%));
    background-color: var(--themeColorSecondary);
  }
}

.FormToggle__input {
  position: absolute;
  top: 0;
  left: 0;

  opacity: 0;
  appearance: none;
}
</style>
